import { checkoutHoursAllow, initOpeningHours } from './opening-hours.js';

const cartEvent = 'latebite:cart-refresh';
const reducedMotion = () => document.documentElement.dataset.motion === 'off' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const cartRoot = () => {
  const value = document.body.dataset.cartRoot || '/';
  return /^\/(?!\/)[^?#\\]*$/.test(value) ? value.replace(/\/+$/, '') + '/' : '/';
};
const validCart = cart => Number.isSafeInteger(cart?.item_count) && cart.item_count >= 0 && Number.isSafeInteger(cart.total_price) && cart.total_price >= 0;
const emitCart = cart => document.dispatchEvent(new CustomEvent(cartEvent, { detail: { cart, source: 'cart-page' } }));

export function initMobileBag(root = document, fetcher = globalThis.fetch) {
  const bag = root.querySelector('[data-mobile-bag]');
  if (!bag) return () => {};
  let alive = true, revision = 0;
  const onDirty = () => {
    revision += 1;
    bag.hidden = true;
    document.body.classList.remove('has-mobile-bag');
  };
  const onCart = event => {
    const cart = event.detail?.cart;
    if (!validCart(cart)) return;
    const currency = cart.currency || bag.dataset.currency;
    if (!/^[A-Z]{3}$/.test(currency || '')) return;
    let total;
    const language = document.documentElement.lang || 'en';
    const locale = currency === 'CHF' && !language.includes('-') ? `${language}-CH` : language;
    try { total = new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'code' }).format(cart.total_price / 100); }
    catch { return; }
    revision += 1;
    bag.querySelector('[data-mobile-bag-total]').textContent = total;
    bag.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = String(cart.item_count); });
    bag.hidden = cart.item_count === 0 || Boolean(root.querySelector('[data-cart-section]'));
    document.body.classList.toggle('has-mobile-bag', !bag.hidden);
  };
  const onPageShow = async event => {
    if (!event.persisted || !fetcher) return;
    const requestRevision = revision;
    try {
      const response = await fetcher(`${cartRoot()}cart.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      if (alive && revision === requestRevision) onCart({ detail: { cart } });
    } catch {}
  };
  if (root.querySelector('[data-cart-section]')) bag.hidden = true;
  document.body.classList.toggle('has-mobile-bag', !bag.hidden);
  document.addEventListener(cartEvent, onCart);
  document.addEventListener('latebite:cart-dirty', onDirty);
  window.addEventListener('pageshow', onPageShow);
  return () => { alive = false; document.removeEventListener(cartEvent, onCart); document.removeEventListener('latebite:cart-dirty', onDirty); window.removeEventListener('pageshow', onPageShow); document.body.classList.remove('has-mobile-bag'); };
}

export function initProductGalleries(root = document) {
  const cleanups = [...root.querySelectorAll('[data-product-gallery]')].map(gallery => {
    const track = gallery.querySelector('[data-gallery-track]');
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    const controls = gallery.querySelector('[data-gallery-controls]');
    if (!track || !controls || slides.length < 2) return () => {};
    const previous = controls.querySelector('[data-gallery-previous]'), next = controls.querySelector('[data-gallery-next]');
    const counter = controls.querySelector('[data-gallery-current]');
    let current = 0, frame = 0, targetIndex, scrollTimer;
    const update = index => {
      current = Math.max(0, Math.min(slides.length - 1, index));
      counter.textContent = String(current + 1);
      previous.disabled = current === 0;
      next.disabled = current === slides.length - 1;
    };
    const readPosition = () => {
      frame = 0;
      const bounds = track.getBoundingClientRect();
      const center = bounds.left + bounds.width / 2;
      let nearest = 0, distance = Infinity;
      slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const delta = Math.abs(rect.left + rect.width / 2 - center);
        if (delta < distance) { nearest = index; distance = delta; }
      });
      update(nearest);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(readPosition);
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => { targetIndex = undefined; readPosition(); }, 120);
    };
    const go = (index, animate) => {
      update(index);
      targetIndex = current;
      const target = slides[current].getBoundingClientRect(), viewport = track.getBoundingClientRect();
      track.scrollTo({ left: track.scrollLeft + target.left - viewport.left, behavior: animate && !reducedMotion() ? 'smooth' : 'instant' });
    };
    const onClick = event => {
      if (event.target.closest('[data-gallery-previous]')) go((targetIndex ?? current) - 1, event.detail > 0);
      if (event.target.closest('[data-gallery-next]')) go((targetIndex ?? current) + 1, event.detail > 0);
    };
    const onKey = event => {
      if (event.target !== track || event.altKey || event.ctrlKey || event.metaKey) return;
      const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
      const targets = { ArrowLeft: current - direction, ArrowRight: current + direction, Home: 0, End: slides.length - 1 };
      if (!(event.key in targets)) return;
      event.preventDefault(); go(targets[event.key], false);
    };
    const onResize = () => go(current, false);
    controls.hidden = false;
    update(0);
    controls.addEventListener('click', onClick);
    track.addEventListener('keydown', onKey);
    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => { cancelAnimationFrame(frame); clearTimeout(scrollTimer); controls.removeEventListener('click', onClick); track.removeEventListener('keydown', onKey); track.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onResize); };
  });
  return () => cleanups.forEach(cleanup => cleanup());
}

const identity = (variant, properties, plan) => {
  const entries = Array.isArray(properties) ? properties : Object.entries(properties || {});
  return JSON.stringify([String(variant), entries.map(([key, value]) => [String(key), String(value)]).sort((a, b) => a[0].localeCompare(b[0])), String(plan || '')]);
};

export function initCartQuantities(root = document, fetcher = globalThis.fetch) {
  const section = root.querySelector('[data-cart-section]');
  if (!section || !fetcher) return () => {};
  const sectionId = section.dataset.sectionId;
  const cartUrl = section.dataset.cartUrl || `${cartRoot()}cart`;
  const pending = new Map();
  let records = new Map(), timer, controller, running = false, failed = false, alive = true, activeRecord;
  let adjusted = false;
  const text = (key, fallback) => section.dataset[key] || fallback;
  const status = message => { const node = section.querySelector('[data-cart-status]'); if (node) node.textContent = message; };
  const inputs = () => [...section.querySelectorAll('[data-cart-quantity]')];
  const invalid = () => inputs().some(input => !input.checkValidity());
  function readRecords() {
    records = new Map([...section.querySelectorAll('[data-cart-line]')].map(row => {
      let properties = {};
      try { properties = JSON.parse(row.dataset.properties || '{}'); } catch {}
      const key = row.dataset.cartLine;
      return [key, { key, identity: identity(row.dataset.variantId, properties, row.dataset.sellingPlan), confirmed: Number(row.querySelector('[data-cart-quantity]').value) }];
    }));
    section.querySelectorAll('[data-cart-step]').forEach(button => { button.hidden = false; });
  }
  function state() {
    const busy = running || pending.size > 0;
    section.setAttribute('aria-busy', String(busy));
    section.querySelectorAll('[data-cart-checkout]').forEach(button => { button.disabled = failed || busy || invalid() || document.body.dataset.ordering !== 'true' || !checkoutHoursAllow(); });
    section.querySelectorAll('[data-cart-update]').forEach(button => { button.disabled = failed || busy; });
    section.querySelectorAll('[data-cart-line]').forEach(row => {
      const record = records.get(row.dataset.cartLine);
      const updating = pending.has(record) || activeRecord === record;
      row.classList.toggle('is-updating', updating);
      const removing = activeRecord === record && activeRecord?.removing;
      row.querySelectorAll('[data-cart-quantity],[data-cart-step]').forEach(control => { control.disabled = failed || Boolean(removing) || Boolean(record?.removed); });
    });
    if (busy) status(text('updating', 'Updating your bag…'));
    else if (!failed && section.querySelector('[data-cart-status]')?.textContent === text('updating', 'Updating your bag…')) status('');
  }
  function queue(input, immediate = false) {
    const record = records.get(input.closest('[data-cart-line]')?.dataset.cartLine);
    if (!record || failed) return;
    if (!input.checkValidity()) { pending.delete(record); state(); return; }
    const quantity = Number(input.value);
    if (quantity === record.confirmed && activeRecord !== record) pending.delete(record);
    else pending.set(record, quantity);
    clearTimeout(timer); state();
    if (!running && pending.size) timer = setTimeout(drain, immediate ? 0 : 300);
  }
  async function request(url, options) {
    controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try { return await fetcher(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal }); }
    finally { clearTimeout(timeout); }
  }
  function reconcile(cart, changed, quantity) {
    const exact = new Map([...records.values()].map(record => [record, cart.items.find(candidate => candidate.key === record.key)]));
    const claimed = new Set([...exact.values()].filter(Boolean).map(item => item.key));
    for (const record of records.values()) {
      let item = exact.get(record);
      if (!item && !(record === changed && quantity === 0)) {
        const matches = cart.items.filter(candidate => !claimed.has(candidate.key) && identity(candidate.variant_id, candidate.properties, candidate.selling_plan_allocation?.selling_plan?.id) === record.identity);
        if (matches.length === 1) item = matches[0];
      }
      if (item) { claimed.add(item.key); record.key = item.key; record.confirmed = item.quantity; }
      else {
        record.confirmed = 0; record.removed = true;
        if (pending.has(record) || (record === changed && quantity !== 0)) throw new Error(text('updateError', 'Reload your bag to check the latest quantities.'));
      }
    }
    if (changed.confirmed !== quantity) adjusted = true;
  }
  async function renderCart(cart) {
    let html = cart.sections?.[sectionId];
    if (typeof html !== 'string') {
      const url = new URL(cartUrl, window.location.origin); url.searchParams.set('section_id', sectionId);
      const response = await request(url.pathname + url.search, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(text('updateError', 'Reload your bag to check the latest quantities.'));
      html = await response.text();
    }
    if (!alive) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const replacement = parsed.querySelector('[data-cart-section]');
    if (!replacement || replacement.dataset.sectionId !== sectionId) throw new Error(text('updateError', 'Reload your bag to check the latest quantities.'));
    if (pending.size) return;
    const focused = section.contains(document.activeElement) ? document.activeElement : null;
    const row = focused?.closest('[data-cart-line]');
    const focusRecord = records.get(row?.dataset.cartLine);
    const focusIndex = row ? [...row.querySelectorAll('a,button,input')].indexOf(focused) : -1;
    const invalidValues = inputs().filter(input => !input.checkValidity()).map(input => [records.get(input.closest('[data-cart-line]').dataset.cartLine)?.key, input.value]);
    replacement.querySelectorAll('script,iframe,object,embed').forEach(node => node.remove());
    section.replaceChildren(...[...replacement.childNodes].map(node => document.importNode(node, true)));
    readRecords();
    invalidValues.forEach(([key, value]) => { const input = [...section.querySelectorAll('[data-cart-line]')].find(node => node.dataset.cartLine === key)?.querySelector('[data-cart-quantity]'); if (input) input.value = value; });
    if (focused) {
      const newRow = [...section.querySelectorAll('[data-cart-line]')].find(node => node.dataset.cartLine === focusRecord?.key);
      const target = newRow?.querySelectorAll('a,button,input')[focusIndex] || section.querySelector('[data-cart-quantity]') || section.querySelector('[data-cart-heading]');
      target?.focus({ preventScroll: true });
    }
  }
  async function drain() {
    if (!alive || running || failed || !pending.size) return;
    running = true; adjusted = false;
    try {
      while (pending.size && alive) {
        const [record, quantity] = pending.entries().next().value;
        pending.delete(record); activeRecord = record; record.removing = quantity === 0; state();
        const response = await request(`${cartRoot()}cart/change.js`, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ id: record.key, quantity, sections: [sectionId], sections_url: cartUrl }) });
        const cart = await response.json();
        if (!response.ok) throw Object.assign(new Error(), { publicMessage: typeof cart.description === 'string' ? cart.description : '' });
        if (!validCart(cart) || !Array.isArray(cart.items)) throw new Error(text('updateError', 'Reload your bag to check the latest quantities.'));
        if (!alive) return;
        reconcile(cart, record, quantity);
        activeRecord = undefined;
        root.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = String(cart.item_count); });
        emitCart(cart);
        if (!pending.size) await renderCart(cart);
      }
      if (alive) status(adjusted ? text('adjusted', 'Your bag was updated to the available quantity. Please review it before checkout.') : text('updated', 'Your bag is up to date.'));
    } catch (error) {
      if (alive) {
        failed = true; pending.clear();
        const fallback = text('updateError', 'We couldn’t confirm this update. Reload your bag to check the latest quantities.');
        status(error.publicMessage ? `${error.publicMessage} ${fallback}` : fallback);
        const recovery = section.querySelector('[data-cart-recovery]'); if (recovery) recovery.hidden = false;
      }
    } finally {
      running = false; activeRecord = undefined;
      if (alive) state();
    }
  }
  const onInput = event => { if (event.target.matches('[data-cart-quantity]')) queue(event.target); };
  const onClick = event => {
    const control = event.target.closest('[data-cart-step],[data-cart-remove]');
    if (!control || !section.contains(control)) return;
    if (control.matches('[data-cart-remove]') && (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)) return;
    event.preventDefault();
    const input = control.closest('[data-cart-line]').querySelector('[data-cart-quantity]');
    if (failed || input.disabled) return;
    const quantity = Number(input.value);
    input.value = control.matches('[data-cart-remove]') ? '0' : String(Math.max(0, Math.min(99, (Number.isSafeInteger(quantity) ? quantity : 0) + Number(control.dataset.cartStep))));
    queue(input, control.matches('[data-cart-remove]'));
  };
  const onSubmit = event => {
    if (!event.target.matches('[data-cart-form]')) return;
    if (event.submitter?.name === 'checkout') {
      if (running || pending.size || failed || invalid() || document.body.dataset.ordering !== 'true' || !checkoutHoursAllow()) event.preventDefault();
      return;
    }
    event.preventDefault();
    if (failed) return;
    inputs().forEach(input => queue(input));
    clearTimeout(timer);
    if (event.target.reportValidity()) void drain();
  };
  readRecords(); state();
  section.addEventListener('input', onInput);
  section.addEventListener('change', onInput);
  section.addEventListener('click', onClick);
  section.addEventListener('submit', onSubmit);
  document.addEventListener('latebite:hours-change', state);
  return () => { alive = false; clearTimeout(timer); controller?.abort(); section.removeEventListener('input', onInput); section.removeEventListener('change', onInput); section.removeEventListener('click', onClick); section.removeEventListener('submit', onSubmit); document.removeEventListener('latebite:hours-change', state); };
}

function boot() {
  window.__nightShoppingCleanup?.();
  const cleanups = [initMobileBag(), initProductGalleries(), initCartQuantities(), initOpeningHours()];
  window.__nightShoppingCleanup = () => cleanups.forEach(cleanup => cleanup());
}
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
  document.addEventListener('shopify:section:load', boot);
}
